// @ts-strict-ignore
import { BufferGeometry, CylinderGeometry, Group, Material, Mesh, MeshBasicMaterial, PlaneGeometry } from 'three';
import { Dictionary } from 'typescript-collections';
import { RGBColorValueVO, SimpleTextValueVO } from '@shared/vo/meta-element.vo';
import { TextureUtils } from '@3d-scene/model3d/util/texture-utils';
import { WireBridgeService } from '@project/components/article/manager/wire-bridge.service';
import { Object3DUtils } from '../common/util/object-3d-utils';
import { ArticleContainer } from './article-container';

type LabeledFlagGeometries = {
  base: BufferGeometry;
  pole: BufferGeometry;
  verticalFlagFront: BufferGeometry;
  verticalFlagBack: BufferGeometry;
  flagHorizontal: BufferGeometry;
}

export class LabeledFlag extends Group {

  public static readonly FLAG_LENGTH: number = 65;
  public static readonly FLAG_LABEL_FONT_SCALE: number = 2;
  private static readonly FLAG_WIDTH: number = 60;
  private static readonly FLAG_HEIGHT: number = 25;


  private static readonly measurementsToLabeledFlagGeometries: Dictionary<string, LabeledFlagGeometries> =
  new Dictionary<string, LabeledFlagGeometries>();
  private static readonly FONT_SIZE: number = 14;
  private readonly base: Mesh;
  private readonly pole: Mesh;

  private readonly verticalFlagMeshFront: Mesh;
  private readonly verticalFlagMeshBack: Mesh;
  private readonly topFlagMesh: Mesh;

  private readonly selectedVerticalFlagmeshFront: Mesh;
  private readonly selectedVerticalFlagmeshBack: Mesh;
  private readonly selectedTopFlagmesh: Mesh;

  private static createLabeledFlagGeometries(radius: number): LabeledFlagGeometries {
    return {
      base: new CylinderGeometry(radius, radius, 30, 8, 1),
      pole: new CylinderGeometry(2, 2, LabeledFlag.FLAG_LENGTH, 8, 1, true),
      verticalFlagFront: new PlaneGeometry(LabeledFlag.FLAG_WIDTH, LabeledFlag.FLAG_HEIGHT, 1, 1),
      verticalFlagBack: new PlaneGeometry(LabeledFlag.FLAG_WIDTH, LabeledFlag.FLAG_HEIGHT, 1, 1).rotateY(Math.PI),
      flagHorizontal: new PlaneGeometry(LabeledFlag.FLAG_WIDTH, LabeledFlag.FLAG_HEIGHT, 1, 1),
    }
  }

  public get container(): ArticleContainer {
    return this._container;
  }

  public constructor(
    private readonly _container: ArticleContainer,
    private label: string,
    material: Material,
    radius: number = 20,
    private createTextures: boolean = true,
  ) {
    super();
    const measurementsKey: string = LabeledFlag.FLAG_LENGTH.toString() + '#' + LabeledFlag.FLAG_WIDTH.toString() + '#' + LabeledFlag.FLAG_HEIGHT.toString();
    let labeledFlagGeometries: LabeledFlagGeometries = LabeledFlag.measurementsToLabeledFlagGeometries.getValue(measurementsKey);
    if (!labeledFlagGeometries) {
      labeledFlagGeometries = LabeledFlag.createLabeledFlagGeometries(radius);
      LabeledFlag.measurementsToLabeledFlagGeometries.setValue(measurementsKey, labeledFlagGeometries);
    }

    this.base = new Mesh(labeledFlagGeometries.base, new MeshBasicMaterial({ color: 0x000000 }));
    this.base.translateY(5);
    this.base.material = material;
    this.base.name = WireBridgeService.FLAG_OBJECT_NAME;
    this.add(this.base);

    this.pole = new Mesh(labeledFlagGeometries.pole, new MeshBasicMaterial({ color: 0x000000 }));
    this.pole.translateY(LabeledFlag.FLAG_HEIGHT);
    this.pole.material = material;
    this.pole.name = WireBridgeService.FLAG_OBJECT_NAME;
    this.add(this.pole);

    const textValueVO: SimpleTextValueVO = new SimpleTextValueVO();
    textValueVO.text = this.label;
    textValueVO.bgColor = new RGBColorValueVO(255,255,255);

    this.verticalFlagMeshFront = new Mesh(labeledFlagGeometries.verticalFlagFront);
    this.verticalFlagMeshFront.translateX(-(LabeledFlag.FLAG_WIDTH / 2));
    this.verticalFlagMeshFront.translateY(LabeledFlag.FLAG_HEIGHT * 2);
    this.verticalFlagMeshFront.name = WireBridgeService.FLAG_OBJECT_NAME;
    this.selectedVerticalFlagmeshFront = this.verticalFlagMeshFront.clone() as Mesh;
    this.selectedVerticalFlagmeshFront.name = ""
    this.add(this.verticalFlagMeshFront, this.selectedVerticalFlagmeshFront);

    this.verticalFlagMeshBack = new Mesh(labeledFlagGeometries.verticalFlagBack);
    this.verticalFlagMeshBack.translateX(-(LabeledFlag.FLAG_WIDTH / 2));
    this.verticalFlagMeshBack.translateY(LabeledFlag.FLAG_HEIGHT * 2);
    this.verticalFlagMeshBack.name = WireBridgeService.FLAG_OBJECT_NAME;
    this.selectedVerticalFlagmeshBack = this.verticalFlagMeshBack.clone() as Mesh;
    this.selectedVerticalFlagmeshBack.name = ""
    this.add(this.verticalFlagMeshBack, this.selectedVerticalFlagmeshBack);

    this.topFlagMesh = new Mesh(labeledFlagGeometries.flagHorizontal);
    this.topFlagMesh.translateX(-(LabeledFlag.FLAG_WIDTH / 2));
    this.topFlagMesh.translateY(LabeledFlag.FLAG_HEIGHT * 2 + 13);
    this.topFlagMesh.rotateX(-Math.PI / 2);
    this.topFlagMesh.rotateZ(Math.PI);
    this.topFlagMesh.name = WireBridgeService.FLAG_OBJECT_NAME;
    this.selectedTopFlagmesh = this.topFlagMesh.clone() as Mesh;
    this.selectedTopFlagmesh.name = ""
    this.add(this.topFlagMesh, this.selectedTopFlagmesh);

    if (this.createTextures) {
      const addTexture = (mesh: Mesh, color: number) => {
        TextureUtils.createTexture(mesh, textValueVO, false, color, false,
          LabeledFlag.FLAG_LABEL_FONT_SCALE * LabeledFlag.FONT_SIZE);
      }
      addTexture(this.verticalFlagMeshFront, 0xff0000);
      addTexture(this.verticalFlagMeshBack, 0xff0000);
      addTexture(this.selectedVerticalFlagmeshFront, 0x60ba00);
      addTexture(this.selectedVerticalFlagmeshBack, 0x60ba00);
      addTexture(this.topFlagMesh, 0xff0000);
      addTexture(this.selectedTopFlagmesh, 0x60ba00);
    }

    this.selectedTopFlagmesh.visible = false;
    this.selectedVerticalFlagmeshFront.visible = false;
    this.selectedVerticalFlagmeshBack.visible = false;

    Object3DUtils.hideInGeometryRepresentation(this.selectedTopFlagmesh, this.selectedVerticalFlagmeshFront, this.selectedVerticalFlagmeshBack);
  }

  public onSelectionChanged(isSelected: boolean) {
    this.selectedTopFlagmesh.visible = isSelected;
    this.selectedVerticalFlagmeshFront.visible = isSelected;
    this.selectedVerticalFlagmeshBack.visible = isSelected;
    this.topFlagMesh.visible = !isSelected;
    this.verticalFlagMeshFront.visible = !isSelected;
    this.verticalFlagMeshBack.visible = !isSelected;
  }
}
